import { useForm } from "react-hook-form";
import { Form, Button, Row, Col } from "react-bootstrap";
import { UserList } from "@/shared/models/UserList";
import { UserRoles, UserRoleDisplayNames } from "@/shared/Enums";
import { UserRoleConstants } from "@/shared/Constants";
import { PermissionHelper } from "@/shared/utils/Permission.helper";
import RequiredStar from "@/components/RequiredStar";
import { useCommonData } from "@/hooks/useCommonData";

interface UserViewEditFormProps {
  userData?: UserList;
  onSubmit: (data: UserList) => void;
  onCancel: () => void;
}

// Form interface for single role selection
interface UserEditFormData {
  id: number;
  userName: string;
  email: string;
  userRole: string; // Single role as string
  addComment: boolean;
  domain: string;
}

const UserViewEditForm = ({ userData, onSubmit, onCancel }: UserViewEditFormProps) => {
  const availableRoles = [
    UserRoleConstants.UserAdminRole,
    UserRoleConstants.UserReaderRole,
    UserRoleConstants.UserEditorRole,
    UserRoleConstants.UserHandlerRole,
  ];

  const { domainOptions } = useCommonData();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserEditFormData>({
    defaultValues: {
      id: userData?.id || 0,
      userName: userData?.userName || "",
      email: userData?.email || "",
      userRole: userData?.userRole?.[0] || "", // Take first role or empty string
      addComment: PermissionHelper.HasAddCommentPermission(userData?.userPermission) || false,
      domain: userData?.domain || "",
    },
  });

  const selectedUserRole = watch("userRole");

  const handleFormSubmit = (data: UserEditFormData) => {
    // Convert single role back to array for API compatibility
    const submissionData: UserList = {
      ...userData!, // Spread existing user data
      id: userData?.id, // Ensure ID is passed for UpdateUserRoles
      apexId: userData?.apexId || "",
      userName: userData?.userName || "",
      email: userData?.email || "",
      team: userData?.team || "",
      userRole: data.userRole ? [data.userRole] : [],
      userPermission: [PermissionHelper.GenerateAddCommentPermissionDetail(data.addComment)],
      domain: data.domain,
    };

    console.log("📤 Submitting user role update:", submissionData);
    onSubmit(submissionData);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>Apex ID</Form.Label>
            <Form.Control type="text" value={userData?.apexId || ""} readOnly className="bg-light" />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>User Name</Form.Label>
            <Form.Control type="text" value={userData?.userName || ""} readOnly className="bg-light" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={userData?.email || ""} readOnly className="bg-light" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>
              User Role <RequiredStar />
            </Form.Label>
            <Form.Select {...register("userRole", { required: "Please select a user role" })} isInvalid={!!errors.userRole}>
              <option value="">Select a role...</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {UserRoleDisplayNames[role as UserRoles] || role}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.userRole?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {selectedUserRole === UserRoleConstants.UserReaderRole && (
        <Row>
          <Col className="mb-3">
            <Form.Check type="checkbox" id="editComment" label="Add Comment" {...register("addComment")} />
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>
              Domain <RequiredStar />
            </Form.Label>
            <Form.Select {...register("domain", { required: "Please select a domain" })} isInvalid={!!errors.domain}>
              <option value="">Select a domain...</option>
              {domainOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.domain?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex justify-content-end gap-2">
          <Button variant="primary" type="submit">
            Save
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default UserViewEditForm;
